/**
 * The dog (ROUND8 §5, station 5): an unarmed handler and his dog work a beat north of the trench, and a stream
 * runs edge to edge beyond them. One step, and the three rules the standing orders give wherever dogs are: a dog
 * follows your trail; water breaks it; stay downwind. It is ticked when he has waded the stream and come out on
 * the north bank - the only way on to the range, so nobody is held up by it. The numbers are the sim's own
 * (`tuningDogs.ts`), so the course never teaches a figure the game does not use.
 */

import { COURSE } from '../../gen/training';
import { DOGS } from '../../sim/tuningDogs';
import type { World } from '../../sim/types';
import { holdOrPress } from './keys';
import { onGround, type Step } from './stepTypes';

/** Metres a dog smells him from, standing downwind of it (the short side) and upwind (the long one). */
export const NOSE_DOWNWIND = Math.round(DOGS.noseRadius * Math.min(DOGS.downwind, DOGS.upwind));
export const NOSE_UPWIND = Math.round(DOGS.noseRadius * Math.max(DOGS.downwind, DOGS.upwind));

/** Past the watcher and not yet over the stream: the dog station. */
const atDogStation = (w: World): boolean =>
  onGround(w) && w.ranger.z < COURSE.watcher.z && w.ranger.z > COURSE.dogs.stream.north;

/** A live dog on the course is running his trail. */
const onHisTrail = (w: World): boolean => w.enemies.some((e) => e.kind === 'dog' && e.state !== 'dead'
  && e.dog?.mode === 'track');

const dog: Step = {
  id: 'dog', station: 'THE DOG', title: 'LOSE THE DOG',
  text: ({ keys }) => `A DOG FOLLOWS YOUR TRAIL. WATER BREAKS IT: WADE THE STREAM AHEAD ${keys.key('moveUp')}. `
    + 'STAY DOWNWIND OF THE DOG.',
  note: ({ world, keys }) => (onHisTrail(world)
    ? `THE DOG HAS YOUR TRAIL, NOSE DOWN. GET INTO THE WATER - ${holdOrPress(keys, 'run')} TO RUN: A DOG `
      + 'CANNOT KEEP UP WITH A RUNNING MAN.'
    : `THE WIND ARROW SHOWS THE WIND. DOWNWIND OF A DOG IT SMELLS YOU FROM ${NOSE_DOWNWIND} M, UPWIND FROM `
      + `${NOSE_UPWIND} M. SNEAKING AND THE GHILLIE DO NOT FOOL A NOSE.`),
  ready: ({ world }) => atDogStation(world),
  done: ({ facts }) => facts.crossedStream,
};

export const DOG_STEPS: readonly Step[] = [dog];
